import { MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport } from 'components';
import { MprcFilterCountInfo } from 'components/filter';
import { FeedDataDto, FeedItemDto, FeedQuestionQueryOrderBy, FeedType } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { CardManager } from 'views/knowledge/components/card/CardManager';
import { RightPanelBreakpoints } from 'views/knowledge/feed/components/FeedContainer';
import { FeedCustomEventProps, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { FeedQuestionFilter, IFeedQuestionFilterOptions } from "views/knowledge/publication/pages/Question/FeedQuestionFilter";
import { RightPanelContentPage } from '../common/RightPanelContentPage';
import { FeedQuestionBox } from "./FeedQuestionBox";

////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    answerId?: string;
    rightPanelBreakpoint: RightPanelBreakpoints;
    openRightPanel: boolean;
    onCloseRightPanel?: () => void;
}


function isFiltered(filter?: IFeedQuestionFilterOptions): boolean {
    return (filter?.searchText?.length || 0) > 0;
}

////////////////////////////////////////////////////////////////////////////////////////////////

export const QuestionPlaybackViewer: React.FC<IProps> = (props) => {

    const { publicationId, answerId, openRightPanel, rightPanelBreakpoint, onCloseRightPanel } = props;
    const location = useLocation();
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const [loading, setLoading] = useState<boolean>(true);
    const [publication, setPublication] = useState<FeedItemDto>();
    const [viewError, setViewError] = useState<any>();
    const [count, setCount] = useState<number | undefined>(undefined);

    const filtered = useRef<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<IFeedQuestionFilterOptions>({
        orderBy: FeedQuestionQueryOrderBy.BestRated,
        searchText: ""
    });

    ///
    const handleFilterChange = useCallback((filter: IFeedQuestionFilterOptions) => {
        filtered.current = isFiltered(filter);
        setFilterOptions(filter);
    }, []);

    ///
    const handleRefreshData = (data?: FeedDataDto, resetFilter?: boolean) => {
        if (resetFilter) {
            setFilterOptions({
                orderBy: FeedQuestionQueryOrderBy.BestRated,
                searchText: ""
            });
        }
        setCount(data?.totalRows ?? 0);
    };

    ///
    const handleNewItem = (e: FeedCustomEventProps) => {
        if (publicationId) {
            setLoading(true);
            load(publicationId);
        }
    };

    ///
    const load = async (publicationId: string) => {
        let markRead = true;

        // Se a solicitação foi realizada a partir do Feed de Histórico,
        // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
        if (location.state?.hasOwnProperty("previousPathname")) {
            const previousPathname = location.state.previousPathname as string;
            markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
        }

        try {
            const result = await api.v1.knowledgeFeed.getFeedItem({ publicationId, markRead });
            const record = result.data;

            setPublication(record);
        }
        catch (error: any) {
            setViewError(error);
        }
        finally {
            setLoading(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (publicationId != null) {
            setLoading(true);
            load(publicationId);
        }
    }, [publicationId]);

    useEffect(() => {
        subscribe("AnswerCreated", handleNewItem);
        return () => {
            unsubscribe("AnswerCreated", handleNewItem);
        };
    }, []);


    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderCardHeader = useCallback(() => {
        return (
            <>
                {
                    publication ?
                        <>
                            <MprcContainer>
                                <CardManager
                                    feedType={FeedType.ContentPage}
                                    pub={publication}
                                    onNeedResetRowHeight={() => { }}
                                    onReload={() => load(publicationId!)}
                                    onFeedItemChanged={() => { load(publicationId!); }}
                                />
                                <FeedQuestionFilter
                                    answerCount={publication.questionDefinition?.answerCount ?? 0}
                                    filterOptions={filterOptions}
                                    onFilterChange={handleFilterChange}
                                />
                            </MprcContainer>
                            {
                                count != undefined && filtered.current &&
                                <MprcContainer>
                                    <MprcFilterCountInfo
                                        variant={"bodyNotice"}
                                        text={t("Feed.Question.FilterInfoMessage", { count: count ?? 0 })}
                                    />
                                </MprcContainer>
                            }
                        </>
                        : null
                }
            </>
        );
    }, [publication, count]);

    ///
    return (
        <div>
            {
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : <FeedQuestionBox
                            publication={publication!}
                            feedType={FeedType.Answers}
                            cardHeader={RenderCardHeader()}
                            rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                            filter={filterOptions}
                            answerId={answerId}
                            count={count}
                            openRightPanel={openRightPanel}
                            rightPanelBreakpoint={rightPanelBreakpoint}
                            marginRightPanel={0}
                            onRefreshData={handleRefreshData}
                            onCloseRightPanel={onCloseRightPanel}
                        />
            }
        </div>
    );
};
